import React, { useCallback, lazy } from 'react';
import {
  FieldCheckBox,
  FieldDatepicker,
  FieldInput,
  FieldTextArea,
  FormCol,
  FormCol2,
  FormCol4,
  FormRow,
  OptionType,
  useIsFieldRequiredHook,
} from 'motion-components';
import {
  FieldSelectCollector,
  FormFieldCollectionPointDlg,
  RowFieldTooltips,
} from 'shared-pages/components';
import { useFormikContext } from 'formik';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { OrderExtrasTable } from './orderExtras-table';

import { CollectionOrderStatusBadge } from '../collection-order-status-badge';
import { Link } from 'react-router-dom';

import { FieldSelectOriginatedFromState } from 'shared-pages/components/field-select/fs-originated-From';
import { FieldSelectRecordedById } from 'shared-pages/components/field-select/fs-recordedBy';
import { CollectionOrderStates } from 'shared-pages/shared/constants';
import CreateCollectionButton from '../createCollectionButton';
import { isFotokiklosi, isRebatteryLiion } from 'shared-pages/common/constants';
import { OrderApprovalTabs } from './xrb-approval-tabs-flow/order-approval-tabs';
import { FieldSelectCollectionOrderState2 } from 'shared-pages/components/field-select/fs-collection-order-state2';
import { ActionButtonsApprovalFlow } from './xrb-approval-tabs-flow/action-buttons-approval-flow';
import { useCollectionOrderCreate } from './use-collection-order-create';
//import FormFieldsLiIon from './order-form-fields-liion';
const FormFieldsLiIon = lazy(() => import('./order-form-fields-liion'));

const FormFields = ({
  isNew,
  updateRecord,
  refetch,
  data,
  validationSchema = undefined,
  loading,
}) => {
  const { values, setFieldValue, errors, isValid, touched } = useFormikContext<any>();
  const isFieldRequired = useIsFieldRequiredHook(validationSchema);
  const { authStore, appStore } = useRootStore();
  const { t } = useTranslation();

  const { handleGraphqlError } = useHandleError('OrderFormFields');
  function calcUserCanEdit() {
    let canEdit = appStore?.isPlatformOwner || isNew;
    if (isFotokiklosi) {
      if (appStore.isCollector) canEdit = true;
    }
    const canEditReasoning = '';
    return { canEdit, canEditReasoning };
  }
  const { canEdit, canEditReasoning } = calcUserCanEdit();
  const { applyCollectionPointToForm } = useCollectionOrderCreate();
  const onCollectorIdChange = useCallback(
    (option: OptionType): void => {
      if (option.value === null || values.collectorId === option.value) return;
      setFieldValue('collectorId', option.value);
    },
    [values?.collectorId, setFieldValue],
  );

  // Καθορίστηκε Νέο Σημείο Συλλογής => Βρές τον Συλλέκτη που το Εξυπηρετεί
  const onCollectionPointIdChange = useCallback(
    async (valueId: string, _valueName: string): Promise<void> => {
      try {
        await applyCollectionPointToForm(valueId, setFieldValue);
      } catch (error) {
        console.error('Error fetching collection point details:', error);
      }
    },
    [applyCollectionPointToForm, setFieldValue],
  );

  const routeId = data?.routesLines[0]?.route?.id;
  const collectionId = data?.collection?.id;

  // LiIon ReBattery
  if (appStore.isCollectionPoint && isRebatteryLiion) {
    return (
      <FormFieldsLiIon
        isNew={isNew}
        updateRecord={updateRecord}
        refetch={refetch}
        data={data}
        validationSchema={validationSchema}
        appStore={appStore}
        canEdit={canEdit}
        onCollectionPointIdChange={onCollectionPointIdChange}
        isFieldRequired={isFieldRequired}
        errors={errors}
        values={values}
        t={t}
      />
    );
  }
  return (
    <div>
      <FormRow>
        {/* First Row */}
        <FormCol className="col-md">
          <FieldInput
            label={t('Κωδικός')}
            name="code"
            placeholder="Αυτόματη Αρίθμηση"
            disabled={true}
          />
        </FormCol>
        <FormCol className="col-md">
          <FormFieldCollectionPointDlg
            label={t('Σημείο Συλλογής')}
            disabled={!canEdit}
            idFieldName="collectionPointId"
            nameFieldName="collectionpoint.name"
            onChange={onCollectionPointIdChange}
            required={isFieldRequired('collectionPointId', values)}
          />
          {errors.collectionPointId && (
            <div className="invalid-feedback">{errors.collectionPointId as any}</div>
          )}
        </FormCol>
        <FormCol className="col-md">
          <FieldDatepicker
            label={t('Ημερομηνία Aίτησης')}
            name="orderedDate"
            disabled={!canEdit}
            required={isFieldRequired('orderedDate', values)}
          />
        </FormCol>
        <FormCol className="col-md">
          {isNew ? (
            <></>
          ) : (
            <div className="d-flex flex-column align-items-start ">
              <span className="mb-1">{t('Κατάσταση :')}</span>

              <CollectionOrderStatusBadge
                id={data?.statusId}
                value={data?.collectionOrderState.value}
                name={data?.collectionOrderState?.name}
              />
            </div>
          )}
        </FormCol>
      </FormRow>

      <FormRow>
        {/* Second Row */}
        <FormCol className="col-md">
          {!isRebatteryLiion && (
            <FieldSelectCollector
              name="collectorId"
              label={t('Συλλέκτης')}
              onChange={onCollectorIdChange}
              disabled={!(canEdit && appStore.isPlatformOwner)}
              required={isFieldRequired('collectorId', values)}
            />
          )}
        </FormCol>
        <FormCol className="col-md">
          <FieldInput
            label={t('Διεύθυνση')}
            name="collectionpoint.addressLine1"
            placeholder=""
            disabled={true}
          />
        </FormCol>
        <FormCol className="col-md">
          <FieldTextArea label={t('Σημειώσεις')} name="notes" disabled={!canEdit} rows={2} />
        </FormCol>

        <FieldCheckBox
          disabled={!canEdit}
          as={FormCol2}
          label={t('Επείγει')}
          name="isUrgent"
          onLabel={t('ΝΑΙ')}
          offLabel={t('ΟΧΙ')}
          onStyle={'warning'}
          offStyle={'primary'}
        />

        {/* 4th Col */}
      </FormRow>

      {/* 3rd Row */}
      <FormRow>
        <FormCol className="col-md">
          <FieldSelectRecordedById
            name="recordedById"
            label={t('Καταγράφηκε Μέσω')}
            disabled={!canEdit}
          />
        </FormCol>
        <FormCol className="col-md">
          <FieldInput
            label={t('Πόλη')}
            name="collectionpoint.addressCity"
            placeholder=""
            disabled={true}
          />
        </FormCol>

        <FormCol className="col-md">
          <FieldInput
            label={t('Υπεύθυνος')}
            name="contactPerson"
            placeholder=""
            disabled={!canEdit}
          />
        </FormCol>

        {/* Εμφάνιση ΔΑ μόνο όταν η αίτηση έχει δηλωθεί από οδηγό */}
        {(values?.statusId === CollectionOrderStates.CollectedByDriver ||
          data?.statusId === CollectionOrderStates.CollectedByDriver) && (
          <FormCol className="col-md">
            <FieldInput label={t('Αριθμός Δελτίου Αποστολής')} name="finDispNote" disabled={true} />
          </FormCol>
        )}

        <FormCol className="col-md"></FormCol>
      </FormRow>
      {/* 4rd Row */}
      <FormRow>
        <FormCol className="col-md">
          <FieldSelectOriginatedFromState
            name="originatedFromId"
            label={t('Δημιουργήθηκε Από')}
            disabled={!canEdit}
          />
        </FormCol>

        <FormCol className="col-md">
          <FieldInput
            label={t('Τ.Κ')}
            name="collectionpoint.postalCode"
            placeholder=""
            disabled={true}
          />
        </FormCol>
        <FormCol className="col-md">
          {collectionId && data?.statusId === CollectionOrderStates.Executed && (
            <>
              <Link to={`/collection/${collectionId}`} className="d-block mb-1">
                {t('Συλλογή :')}
              </Link>
              <FieldInput
                name="collection.code"
                placeholder=""
                disabled={true}
                style={{ marginTop: '2px' }}
              />
            </>
          )}
        </FormCol>

        {!isRebatteryLiion && (
          <FormCol className="col-md d-flex align-items-center justify-content-between">
            <CreateCollectionButton
              collectionOrderId={data?.id}
              disabled={data?.statusId !== CollectionOrderStates.NotRouted}
            />

            <RowFieldTooltips data={data} showLegacy={false} />
          </FormCol>
        )}
      </FormRow>

      {/* 5th Row */}

      <FormRow>
        <FormCol className="col-md">
          {routeId && (
            <>
              <Link to={`/route/${routeId}`} className="d-block mb-1">
                {t('Δρομολόγιο :')}
              </Link>
              <FieldInput name="routesLines[0].route.code" placeholder="" disabled={true} />
            </>
          )}
        </FormCol>
        <FormCol className="col-md">
          {routeId && (
            <FieldDatepicker
              label={t('Ημερομηνία Δρομολογίου')}
              name="routesLines[0].route.routeDate"
              disabled={!canEdit}
            />
          )}
        </FormCol>
      </FormRow>

      {/* Συσκευασίες - Containers */}
      {!isRebatteryLiion && (
        <div className="mb-5">
          {values && values?.collectionOrdersContainers && (
            <OrderExtrasTable data={values.collectionOrdersContainers} canEdit={canEdit} />
          )}
        </div>
      )}

      {/* Διαδικασία Αίτησης ReBattery  */}

      {isRebatteryLiion && !loading && (
        <>
          <ActionButtonsApprovalFlow isNew={isNew} updateRecord={updateRecord} />
          <div className="mt-4 mb-5">
            <OrderApprovalTabs data={values} isNew={isNew} refetch={refetch} />
          </div>
        </>
      )}
    </div>
  );
};

export default FormFields;
